import { number, z } from 'zod';
import {
  BasicQuery,
  TimeFrame,
  EntityReference,
  Metric,
  Runtime,
  ThresholdFilter,
} from './webapp-common.dto';
import { Deployments } from './webapp-common.dto';
import { GraphsData } from './webapp-common.dto';

export const Endpoint = z.object({
  id: z.number(),
  path: z.string(),
  method: z.string(),
});
export type Endpoint = z.infer<typeof Endpoint>;

export const FunctionMetrics = z.object({
  runs: Metric,
  normalizedRuns: Metric,
  durationAvg: Metric,
  durationP90: Metric,
  errorRate: Metric,
  cost: Metric,
});
export type FunctionMetrics = z.infer<typeof FunctionMetrics>;

export const ContextCallbackArgs = z.object({
  type: z.string(),
  value: z.string(),
  index: z.number(),
});
export type ContextCallbackArgs = z.infer<typeof ContextCallbackArgs>;

export const FunctionScopeChain = z.object({
  type: z.string(),
  symbolName: z.string().optional(),
  contextName: z.string().optional(),
});
export type FunctionScopeChain = z.infer<typeof FunctionScopeChain>;

export const FunctionIdentity = z.object({
  isAsync: z.boolean().nullable(),
  contextCallbackName: z.string(),
  contextCallbackArgs: z.array(ContextCallbackArgs),
  contextCallbackArgsCount: z.number(),
  contextCallbackIndex: z.number(),
  contextName: z.string(),
  methodKind: z.string(),
  scopeChain: z.array(FunctionScopeChain),
  params: z.string(),
});
export type FunctionIdentity = z.infer<typeof FunctionIdentity>;

export const FunctionData = z.object({
  id: z.number(),
  name: z.string(),
  filePath: z.string(),
  signature: z.string(),
  firstSeenAt: z.coerce.date(),
  lastInvokedAt: z.coerce.date().nullable(),
  lastChangeAt: z.coerce.date().nullable(),
  totalServices: z.number(),
  services: z.array(EntityReference),
  totalWorkloads: z.number(),
  workloads: z.array(EntityReference),
  totalEndpoints: z.number(),
  endpoints: z.array(Endpoint),
  metrics: FunctionMetrics,
  repositoryName: z.string().nullable().optional(),
  lineNumber: z.number(),
  runtime: Runtime,
  identity: FunctionIdentity,
  thirdPartyModule: z.string().optional(),
});
export type FunctionData = z.infer<typeof FunctionData>;

export const ErrorBreakdownItem = z.object({
  name: z.string(),
  metrics: z.object({
    count: Metric,
    normalizedCount: Metric,
    distribution: Metric,
  }),
  lastSeenAt: z.coerce.date().optional(),
});
export type ErrorBreakdownItem = z.infer<typeof ErrorBreakdownItem>;

export const ErrorBreakdown = z.array(ErrorBreakdownItem);
export type ErrorBreakdown = z.infer<typeof ErrorBreakdown>;

export const CallerFunctionEnumType = z.enum(['WRAPPED', 'TOP_LEVEL', 'THIRD_PARTY']);
export type CallerFunctionEnumType = z.infer<typeof CallerFunctionEnumType>;

export const InvocationSourcesFunctionData = FunctionData.pick({
  id: true,
  name: true,
  signature: true,
  lastInvokedAt: true,
  metrics: true,
  filePath: true,
  lineNumber: true,
  repositoryName: true,
}).extend({
  firstInvokedAt: z.coerce.date(),
  callerFunctionType: CallerFunctionEnumType.optional(),
  errors: ErrorBreakdown,
  thirdPartyModule: z.string().optional(),
});
export type InvocationSourcesFunctionData = z.infer<typeof InvocationSourcesFunctionData>;

export const FunctionSortableKeys = FunctionData.pick({
  id: true,
  name: true,
  signature: true,
  filePath: true,
  firstSeenAt: true,
  lastInvokedAt: true,
  lastChangeAt: true,
  totalServices: true,
  totalEndpoints: true,
  runtime: true,
})
  .extend({
    'runs.value': z.string(),
    'runs.trend': z.string(),
    'normalizedRuns.value': z.string(),
    'durationAvg.value': z.string(),
    'durationAvg.trend': z.string(),
    'durationP90.value': z.string(),
    'durationP90.trend': z.string(),
    'errorRate.value': z.string(),
    'errorRate.trend': z.string(),
    runs: z.string(),
    durationAvg: z.string(),
    durationP90: z.string(),
    errorRate: z.string(),
  })
  .keyof();
export type FunctionSortableKeys = z.infer<typeof FunctionSortableKeys>;

export const OrderByQuery = z.object({
  key: FunctionSortableKeys,
  sortOrder: z.union([z.literal('asc'), z.literal('desc')]),
});
export type OrderByQuery = z.infer<typeof OrderByQuery>;

export const GetFunctionsQuery = BasicQuery.extend({
  orderBy: OrderByQuery.optional(),
  includeGhost: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((value) => value === true || value === 'true')
    .optional(),
  includeUnnamed: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((value) => value === true || value === 'true')
    .optional(),
  timeFrame: TimeFrame.optional(),
  serviceIds: z.array(z.coerce.number()).optional(),
  filterByAPI: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((value) => value === true || value === 'true')
    .optional(),
  filePaths: z.array(z.string()).optional(),
  runtimes: z.array(Runtime).optional(),
  normalizedRuns: ThresholdFilter.optional(),
  durationAvg: ThresholdFilter.optional(),
  errorRate: ThresholdFilter.optional(),
  tags: z.record(z.string()).optional(),
  flowDeployments: z.array(z.string()).optional(),
});
export type GetFunctionsQuery = z.infer<typeof GetFunctionsQuery>;

export const GetFunctionQuery = z.object({
  timeFrame: TimeFrame.optional(),
  environmentName: z.string().optional(),
  flowId: z.coerce.number().optional(),
  serviceId: z.coerce.number().optional(),
  tags: z.record(z.string()).optional(),
});
export type GetFunctionQuery = z.infer<typeof GetFunctionQuery>;

export const GetFunctionsResponse = z.object({
  total: z.number(),
  countFiltered: z.number(), // if no filter is applied, this will be the same as total
  functions: z.array(FunctionData),
});
export type GetFunctionsResponse = z.infer<typeof GetFunctionsResponse>;

export const Caller = InvocationSourcesFunctionData.extend({
  metrics: FunctionMetrics.extend({
    distribution: Metric,
  }),
  identity: FunctionIdentity,
});
export type Caller = z.infer<typeof Caller>;
export const Callers = z.array(Caller);
export type Callers = z.infer<typeof Callers>;

export const Callee = InvocationSourcesFunctionData.extend({
  metrics: FunctionMetrics.extend({
    rate: Metric,
  }),
  identity: FunctionIdentity,
});
export type Callee = z.infer<typeof Callee>;
export const Callees = z.array(Callee);
export type Callees = z.infer<typeof Callees>;

export const GitHistory = z.array(
  z.object({
    reference: EntityReference, //name=link to github
    commit: z.string(),
    committedAt: z.coerce.date(),
    author: z.string(),
    changedFunctions: number(), // might become a list of function references
  }),
);
export type GitHistory = z.infer<typeof GitHistory>;

export const EndpointByFunction = z.object({
  //TODO: legacy type, remove after all clients upgrade extension version from v1.1.32 in vscode & v1.0.1 in JB (28.11.2024)
  id: z.number(),
  path: z.string(),
  methods: z.array(z.string()),
  runs: z.number(),
  services: z.array(EntityReference), // TODO: remove after all clients upgrade extension versions (18.12.2024)
  service: EntityReference,
});

export type EndpointByFunction = z.infer<typeof EndpointByFunction>;

export const BaseFlowByFunction = z.object({
  id: z.number(),
  runs: z.number(),
  service: EntityReference,
  type: z.string(),
  last_invoked_at: z.coerce.date().optional().nullable(),
  first_invoked_at: z.coerce.date().optional().nullable(),
  distribution: z.number().optional(),
  error_rate: z.number().optional(),
  avg_duration: z.number().optional(),
  normalized_runs: z.number().optional(),
});

export type BaseFlowByFunction = z.infer<typeof BaseFlowByFunction>;

export const HttpEndpointByFunction = BaseFlowByFunction.extend({
  type: z.literal('ENDPOINT'),
  path: z.string(),
  methods: z.array(z.string()),
});
export type HttpEndpointByFunction = z.infer<typeof HttpEndpointByFunction>;

export const KafkaTopicByFunction = BaseFlowByFunction.extend({
  type: z.literal('KAFKA'),
  kafka_topic_name: z.string(),
  kafka_topic_group_id: z.string(),
});
export type KafkaTopicByFunction = z.infer<typeof KafkaTopicByFunction>;

export const SqsQueueByFunction = BaseFlowByFunction.extend({
  type: z.literal('SQS'),
  sqs_queue_name: z.string(),
});
export type SqsQueueByFunction = z.infer<typeof SqsQueueByFunction>;

export const FlowByFunction = z.union([
  HttpEndpointByFunction,
  KafkaTopicByFunction,
  SqsQueueByFunction,
]);

export type FlowByFunction = z.infer<typeof FlowByFunction>;

//guards:
export function isEndpoint(flow: FlowByFunction): flow is HttpEndpointByFunction {
  return flow.type === 'ENDPOINT' || 'path' in flow;
}

export function isHttpEndpoint(
  flow: HttpEndpointByFunction | EndpointByFunction,
): flow is HttpEndpointByFunction {
  return 'service' in flow;
}

export function isKafkaTopic(flow: FlowByFunction): flow is KafkaTopicByFunction {
  return flow.type === 'KAFKA';
}

export function isSqsQueue(flow: FlowByFunction): flow is SqsQueueByFunction {
  return flow.type === 'SQS';
}

export const GetFunctionResponse = z.object({
  function: FunctionData,
  callers: Callers,
  callees: Callees,
  errors: ErrorBreakdown,
  gitHistory: GitHistory,
  graphs: GraphsData,
  deployments: Deployments,
  directEndpoints: z.array(EndpointByFunction), //TODO: legacy type, remove after all clients upgrade extension version from v1.1.32 in vscode & v1.0.1 in JB (28.11.2024)
  allFlows: z.array(EndpointByFunction), //TODO: legacy type, remove after all clients upgrade extension version from v1.1.32 in vscode & v1.0.1 in JB (28.11.2024)
  directFlows: z.array(FlowByFunction),
  flows: z.array(FlowByFunction),
});
export type GetFunctionResponse = z.infer<typeof GetFunctionResponse>;

export const FunctionUtilization = FunctionData.pick({
  id: true,
  signature: true,
  filePath: true,
  lastChangeAt: true,
  endpoints: true,
  totalEndpoints: true,
}).extend({
  linesOfCode: z.number(),
  normalizedRuns: z.number(),
});

export type FunctionUtilization = z.infer<typeof FunctionUtilization>;

export const LiveCodeRequest = z.object({
  timeFrame: TimeFrame.optional(),
  environmentName: z.string().optional(),
  serviceId: z.coerce.number(),
});

export type LiveCodeRequest = z.infer<typeof LiveCodeRequest>;

export const LiveCodeResponse = z.object({
  functions: z.array(FunctionUtilization),
});

export type LiveCodeResponse = z.infer<typeof LiveCodeResponse>;

// type guard for Callers and callees
export function isCaller(data: Caller | Callee): data is Caller {
  return 'distribution' in data.metrics;
}
